import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { roomsService, coursesService, addonsService, superAdminService, bookingsService } from '../services/api';
import omgExperienceService from '../services/omgExperienceService';
import { format, addDays, differenceInDays, subDays } from 'date-fns';
import { CounterInput } from './ui';
import { AlarmClock as AlarmClockIcon, BedDouble as BedDoubleIcon, Calendar as CalendarIcon, Flower2 as Flower2Icon, Home as HomeIcon, Palmtree as PalmtreeIcon, RefreshCw as RefreshCwIcon, Shield as ShieldIcon, Sparkles as SparklesIcon } from 'lucide-react';

function SuperAdminPastDateReservation({ user, onReservationCreated }) {
  const [currentStep, setCurrentStep] = useState('course-selection'); // 'course-selection', 'room-selection', 'guest-details'
  
  // State to track OMG Experience mode
  const [isOMGExperienceMode, setIsOMGExperienceMode] = useState(false);
  
  // Add CSS for spinner animation
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  
  // Form data state for course and dates
  const [formData, setFormData] = useState({
    checkInDate: '',
    checkOutDate: '',
    courseId: '',
    courseCount: 1,
    guestCount: 1,
    addons: [],
    notes: '',
    adminNote: '',
    reason: '',
    paymentStatus: 'PENDING'
  });

  // Multiple room selections with guest details
  const [selectedRooms, setSelectedRooms] = useState([]); // Array of { room, bed, guest: { name, email, phone, gender } }
  const [guestDetails, setGuestDetails] = useState([]); // Array of guest details for each room
  const [sameGuestForAll, setSameGuestForAll] = useState(true);
  const [primaryGuest, setPrimaryGuest] = useState({
    name: '',
    email: '',
    phone: '',
    gender: ''
  });

  // Data states
  const [courses, setCourses] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [addons, setAddons] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  
  // Pricing and calculation states
  const [totalPricing, setTotalPricing] = useState({
    individualBookings: [],
    totalAmount: 0,
    courseFee: 0,
    addonsTotal: 0
  });
  const [totalAmountChanged, setTotalAmountChanged] = useState(false);

  // Flash effect when total amount changes
  React.useEffect(() => {
    if (totalPricing.totalAmount > 0) {
      setTotalAmountChanged(true);
      const timer = setTimeout(() => setTotalAmountChanged(false), 500);
      return () => clearTimeout(timer);
    }
  }, [totalPricing.totalAmount]);
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [calculatingPrice, setCalculatingPrice] = useState(false);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [coursesData, addonsData] = await Promise.all([
        coursesService.getAll({ 
          sortBy: 'createdAt', 
          sortOrder: 'desc',
          limit: 100 
        }),
        addonsService.getAll()
      ]);

      // Fix courses data extraction - get courses from data.courses
      const coursesArray = coursesData.data?.courses || coursesData.courses || [];
      setCourses(Array.isArray(coursesArray) ? coursesArray : []);
      
      // Fix addons data extraction - get addons from data.addons or direct array
      const addonsArray = addonsData.data?.addons || addonsData.data || addonsData || [];
      setAddons(Array.isArray(addonsArray) ? addonsArray : []);
      
      console.log('Loaded initial data for past date booking:', {
        coursesCount: coursesArray.length,
        addonsCount: addonsArray.length
      });
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load form data');
      setCourses([]);
      setAddons([]);
    }
  };

  // Load available rooms for past dates using super admin endpoints
  const loadAvailableRooms = useCallback(async () => {
    if (!formData.checkInDate || !formData.checkOutDate) {
      setAvailableRooms([]);
      return;
    }

    setLoadingRooms(true);
    try {
      // Get all rooms first
      const roomsResponse = await roomsService.getAll({ limit: 100 });
      const allRooms = roomsResponse.rooms || roomsResponse.data?.rooms || [];

      const roomsWithAvailability = [];

      for (const room of allRooms) {
        if (room.status !== 'AVAILABLE') continue;

        try {
          // Use super admin past availability check
          const availabilityResponse = await superAdminService.checkPastDateAvailability(
            room.id, 
            formData.checkInDate, 
            formData.checkOutDate
          );
          
          const availability = availabilityResponse?.data?.availability;
          
          // For shared rooms, check if there are any available beds (even if whole room isn't available)
          if (room.roomType?.isShared && availability?.availableBeds && availability.availableBeds.length > 0) {
            const roomData = { ...room };
            roomData.availableBeds = availability.availableBeds.map(bed => ({
              id: bed.bedId,
              bedNumber: bed.bedNumber,
              isFemaleOnly: bed.isFemaleOnly,
              isAvailable: true,
              roomId: room.id
            }));
            
            roomsWithAvailability.push(roomData);
          } else if (!room.roomType?.isShared && availability?.isAvailable) {
            // Private room - only add if fully available
            const roomData = { ...room };
            roomData.isAvailable = true;
            roomsWithAvailability.push(roomData);
          }
        } catch (error) {
          console.warn(`Could not check past date availability for room ${room.id}:`, error);
        }
      }

      console.log('Loaded available rooms (Past Date):', {
        totalRooms: roomsWithAvailability.length,
        roomDetails: roomsWithAvailability.map(r => ({
          id: r.id,
          number: r.roomNumber,
          type: r.roomType?.name,
          isShared: r.roomType?.isShared,
          availableBeds: r.availableBeds?.length || 0
        })),
        isOMGMode: isOMGExperienceMode
      });
      
      // If in OMG mode, log which rooms are OMG-eligible
      if (isOMGExperienceMode) {
        const omgEligibleRooms = roomsWithAvailability.filter(room => 
          omgExperienceService.isSharedRoomType(room.roomType?.name)
        );
        console.log('OMG Eligible Rooms (Past Date):', {
          count: omgEligibleRooms.length,
          rooms: omgEligibleRooms.map(r => ({
            number: r.roomNumber,
            type: r.roomType?.name
          }))
        });
      }
      
      setAvailableRooms(roomsWithAvailability);
    } catch (error) {
      console.error('Error loading available rooms for past dates:', error);
      toast.error('Failed to load available rooms');
      setAvailableRooms([]);
    } finally {
      setLoadingRooms(false);
    }
  }, [formData.checkInDate, formData.checkOutDate, isOMGExperienceMode]);

  // Handle OMG Experience selection
  const handleOMGExperienceSelect = () => {
    console.log('OMG Experience Mode Activated (Past Date)');
    setIsOMGExperienceMode(true);
    setSelectedCourse(null);
    setFormData(prev => ({
      ...prev,
      courseId: '',
      courseCount: 0,
      checkInDate: '',
      checkOutDate: ''
    }));
    setCurrentStep('room-selection');
    toast.info('OMG Experience mode activated for past date booking! Select Daya or Meera Sharing rooms.', {
      autoClose: 4000
    });
  };

  // Handle course selection
  const handleCourseSelect = (course) => {
    console.log('Super Admin Past Date Reservation Course Selection:', {
      selectedCourse: course ? { id: course.id, name: course.name, fee: course.fee } : null,
      isStaycation: !course
    });

    // Reset OMG Experience mode when selecting regular course/staycation
    setIsOMGExperienceMode(false);

    if (course) {
      setSelectedCourse(course);
      setFormData(prev => ({
        ...prev,
        courseId: course.id,
        courseCount: 1, // Reset to 1 for course selections
        checkInDate: '',
        checkOutDate: ''
      }));
    } else {
      // Staycation case - no course selected, set course count to 0
      setSelectedCourse(null);
      setFormData(prev => ({
        ...prev,
        courseId: '',
        courseCount: 0, // Always 0 for staycation
        checkInDate: '',
        checkOutDate: ''
      }));
    }
    setCurrentStep('room-selection');
  };

  // Handle room/bed selection (multiple selections)
  const handleAccommodationSelect = (room, bed = null) => {
    const selectionKey = bed ? `bed-${bed.id}` : `room-${room.id}`;
    
    // Check if already selected
    const alreadySelected = selectedRooms.some(sel => 
      bed ? sel.bed?.id === bed.id : (sel.room.id === room.id && !sel.bed)
    );
    
    if (alreadySelected) {
      toast.info('Already added to selection');
      return;
    }

    const newSelection = { id: selectionKey, room, bed };
    setSelectedRooms(prev => [...prev, newSelection]);
    
    // Initialize guest details for new selection
    setGuestDetails(prev => ([
      ...prev,
      {
        name: sameGuestForAll ? (primaryGuest.name || '') : '',
        email: sameGuestForAll ? (primaryGuest.email || '') : '',
        phone: sameGuestForAll ? (primaryGuest.phone || '') : '',
        gender: bed?.isFemaleOnly ? 'FEMALE' : (sameGuestForAll ? (primaryGuest.gender || '') : '')
      }
    ]));
    
    toast.success(`Added ${bed ? `Bed ${bed.bedNumber}` : `Room ${room.roomNumber}`}`);
  };

  // Remove room selection
  const removeRoomSelection = (index) => {
    setSelectedRooms(prev => prev.filter((_, i) => i !== index));
    setGuestDetails(prev => prev.filter((_, i) => i !== index));
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-calculate checkout date for duration-based courses
    if (name === 'checkInDate' && selectedCourse && selectedCourse.duration && !selectedCourse.startDate) {
      const checkInDate = new Date(value);
      const checkOutDate = new Date(checkInDate);
      checkOutDate.setDate(checkOutDate.getDate() + selectedCourse.duration);
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        checkOutDate: checkOutDate.toISOString().split('T')[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Calculate total pricing
  const calculateTotalPrice = useCallback(async () => {
    if (!formData.checkInDate || !formData.checkOutDate || selectedRooms.length === 0) {
      setTotalPricing({
        individualBookings: [],
        totalAmount: 0,
        courseFee: 0,
        addonsTotal: 0
      });
      return;
    }

    setCalculatingPrice(true);
    try {
      // NEW API FORMAT: Use unified calculate-price endpoint with rooms[] and beds[]
      const rooms = [];
      const beds = [];

      const checkIn = new Date(formData.checkInDate + 'T15:00:00.000Z').toISOString();
      const checkOut = new Date(formData.checkOutDate + 'T11:00:00.000Z').toISOString();

      selectedRooms.forEach((selection) => {
        if (selection.bed) {
          beds.push({
            bedId: selection.bed.id,
            checkIn,
            checkOut
          });
        } else {
          rooms.push({
            roomId: selection.room.id,
            checkIn,
            checkOut
          });
        }
      });

      const courses = formData.courseId ? [{
        courseId: formData.courseId,
        quantity: formData.courseCount || 1
      }] : [];

      const addOns = (formData.addons || []).map(addonId => ({
        addonId,
        quantity: 1
      }));

      const pricingRequest = {
        rooms,
        beds,
        courses,
        addOns,
        guestCount: formData.guestCount || 1
      };

      console.log('Calculating price with new API format:', {
        ...pricingRequest,
        isOMGMode: isOMGExperienceMode
      });
      
      // Use OMG Experience API if in OMG mode
      let response;
      if (isOMGExperienceMode) {
        console.log('Using OMG Experience pricing endpoint (Past Date)');
        response = await omgExperienceService.calculatePrice(pricingRequest);
      } else {
        response = await bookingsService.calculatePrice(pricingRequest);
      }
      const priceBreakdown = response.data || response;

      console.log('Price breakdown response:', priceBreakdown);

      // NEW UNIFIED FORMAT: { totalPrice, breakdown: { rooms, beds, courses, addOns }, pricingType, hasOMGMarkup }
      const breakdown = priceBreakdown.breakdown || {};
      const totalAmount = priceBreakdown.totalPrice || 0;
      const pricingType = priceBreakdown.pricingType || 'regular';
      const hasOMGMarkup = priceBreakdown.hasOMGMarkup || false;
      
      // Calculate course fee from breakdown
      const courseFeeTotal = breakdown.courses?.reduce((sum, course) => 
        sum + (course.total || course.fee || 0), 0) || 0;
      
      // Calculate addons total
      const addonsTotal = breakdown.addOns?.reduce((sum, addon) => 
        sum + (addon.total || 0), 0) || 0;

      // Extract individual bookings for display
      const individualBookings = [];
      
      // Add room costs from breakdown
      if (breakdown.rooms && breakdown.rooms.length > 0) {
        breakdown.rooms.forEach((roomPrice) => {
          const selection = selectedRooms.find(sel => !sel.bed && sel.room.id === roomPrice.roomId);
          if (selection) {
            individualBookings.push({
              type: 'room',
              name: `${selection.room.roomType?.name || 'Room'} ${roomPrice.roomNumber || selection.room.roomNumber}`,
              rate: roomPrice.nights > 0 ? Math.round(roomPrice.basePrice / roomPrice.nights) : 0,
              nights: roomPrice.nights || 0,
              total: roomPrice.total || 0,
              omgMarkup: roomPrice.omgMarkup || 0,
              basePrice: roomPrice.basePrice || 0,
              isOMGPricing: roomPrice.isOMGPricing || false
            });
          }
        });
      }

      // Add bed costs from breakdown
      if (breakdown.beds && breakdown.beds.length > 0) {
        breakdown.beds.forEach((bedPrice) => {
          const selection = selectedRooms.find(sel => sel.bed && sel.bed.id === bedPrice.bedId);
          if (selection) {
            individualBookings.push({
              type: 'bed',
              name: `${bedPrice.roomTypeName || selection.room.roomType?.name || 'Shared'} - Bed ${bedPrice.bedNumber}`,
              rate: bedPrice.nights > 0 ? Math.round(bedPrice.basePrice / bedPrice.nights) : 0,
              nights: bedPrice.nights || 0,
              total: bedPrice.total || 0,
              omgMarkup: bedPrice.omgMarkup || 0,
              basePrice: bedPrice.basePrice || 0,
              isOMGPricing: bedPrice.isOMGPricing || false
            });
          }
        });
      }

      const totalRoomAmount = individualBookings.reduce((sum, b) => sum + b.total, 0);
      const totalBasePrice = individualBookings.reduce((sum, b) => sum + b.basePrice, 0);
      const totalOMGMarkup = individualBookings.reduce((sum, b) => sum + b.omgMarkup, 0);

      setTotalPricing({
        individualBookings,
        totalAmount,
        courseFee: courseFeeTotal,
        addonsTotal,
        roomsTotal: totalRoomAmount,
        totalBasePrice: totalBasePrice,
        totalOMGMarkup: totalOMGMarkup,
        nights: priceBreakdown.rooms?.[0]?.nights || priceBreakdown.beds?.[0]?.nights || 0,
        pricingType: pricingType,
        hasOMGMarkup: hasOMGMarkup,
        markupInfo: priceBreakdown.markupInfo || ''
      });

      console.log('Pricing calculated (new format):', {
        totalAmount,
        breakdown: { totalRoomAmount, courseFeeTotal, addonsTotal },
        roomCount: rooms.length,
        bedCount: beds.length
      });

    } catch (error) {
      console.error('Error calculating price:', error);
      setTotalPricing({
        individualBookings: [],
        totalAmount: 0,
        courseFee: 0,
        addonsTotal: 0
      });
    } finally {
      setCalculatingPrice(false);
    }
  }, [formData.checkInDate, formData.checkOutDate, formData.guestCount, formData.courseCount, formData.addons, formData.courseId, selectedRooms]);

  // Recalculate pricing when dependencies change
  useEffect(() => {
    calculateTotalPrice();
  }, [calculateTotalPrice]);

  // Handle guest details changes
  const handleGuestDetailsChange = (index, field, value) => {
    setGuestDetails(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return updated;
    });
  };

  // Handle primary guest details changes
  const handlePrimaryGuestChange = (field, value) => {
    setPrimaryGuest(prev => {
      const updated = { ...prev, [field]: value };
      if (sameGuestForAll) {
        setGuestDetails(prevGuests => prevGuests.map(g => ({
          ...g,
          [field]: value
        })));
      }
      return updated;
    });
  };

  // Toggle same guest for all
  const handleSameGuestToggle = (checked) => {
    setSameGuestForAll(checked);
    if (checked) {
      setGuestDetails(prev => prev.map(g => ({
        name: primaryGuest.name || g.name || '',
        email: primaryGuest.email || g.email || '',
        phone: primaryGuest.phone || g.phone || '',
        gender: primaryGuest.gender || g.gender || ''
      })));
    }
  };

  const proceedToGuestDetails = () => {
    if (selectedRooms.length === 0) {
      toast.error('Please add at least one room/bed');
      return;
    }
    
    setCurrentStep('guest-details');
  };

  // Handle addon changes
  const handleAddonChange = (addonId, checked) => {
    setFormData(prev => ({
      ...prev,
      addons: checked 
        ? [...prev.addons, addonId]
        : prev.addons.filter(id => id !== addonId)
    }));
  };

  // Handle form submission - create multiple past date bookings
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    // Small delay to ensure all state updates are processed
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Validation
    if (!formData.checkInDate || !formData.checkOutDate) {
      toast.error('Please set check-in and check-out dates');
      return;
    }

    if (!formData.reason || formData.reason.trim().length < 3) {
      toast.error('Please provide a reason for creating past date booking (minimum 3 characters)');
      return;
    }

    if (selectedRooms.length === 0) {
      toast.error('Please select at least one accommodation');
      return;
    }

    // Validate guest details
    let validationGuestDetails = guestDetails;
    if (guestDetails.length !== selectedRooms.length) {
      validationGuestDetails = selectedRooms.map((_, index) => 
        guestDetails[index] || {
          name: sameGuestForAll ? (primaryGuest.name || '') : '',
          email: sameGuestForAll ? (primaryGuest.email || '') : '',
          phone: sameGuestForAll ? (primaryGuest.phone || '') : '',
          gender: sameGuestForAll ? (primaryGuest.gender || '') : ''
        }
      );
      setGuestDetails(validationGuestDetails);
    }

    for (let i = 0; i < selectedRooms.length; i++) {
      const guest = validationGuestDetails[i];
      
      if (!guest || !guest.name || !guest.email || !guest.gender) {
        toast.error(`Please fill in all required fields for Guest ${i + 1} (Name, Email, Gender)`);
        return;
      }
      // Female-only bed enforcement
      const sel = selectedRooms[i];
      if (sel?.bed?.isFemaleOnly && guest.gender !== 'FEMALE') {
        toast.error(`Guest ${i + 1} must be Female for the selected female-only bed.`);
        return;
      }
    }

    setLoading(true);
    try {
      // NEW API FORMAT: Create ONE booking with multiple rooms/beds arrays

      // Separate private rooms from shared beds
      const rooms = [];
      const beds = [];

      const checkIn = new Date(formData.checkInDate + 'T15:00:00.000Z').toISOString();
      const checkOut = new Date(formData.checkOutDate + 'T11:00:00.000Z').toISOString();

      selectedRooms.forEach((selection) => {
        if (selection.bed) {
          // Shared room with bed selection
          beds.push({
            bedId: selection.bed.id,
            checkIn,
            checkOut
          });
        } else {
          // Private room
          rooms.push({
            roomId: selection.room.id,
            checkIn,
            checkOut
          });
        }
      });

      // Primary guest is the booking owner
      const primaryGuestData = validationGuestDetails[0];

      // Build additional guests info for notes
      let guestsNotes = '';
      if (validationGuestDetails.length > 1) {
        const otherGuests = validationGuestDetails.slice(1).map((guest, idx) => {
          return `Guest ${idx + 2}: ${guest.name} (${guest.email}, ${guest.phone || 'No phone'}, ${guest.gender})`;
        });
        guestsNotes = `\n\nAdditional Guests:\n${otherGuests.join('\n')}`;
      }

      // Build courses array
      const courses = formData.courseId ? [{
        courseId: formData.courseId,
        quantity: formData.courseCount || 1
      }] : [];

      // Build addOns array
      const addOns = (formData.addons || []).map(addon => ({
        addonId: addon.id,
        quantity: addon.quantity || 1
      }));

      const reservationData = {
        guestDetails: {
          name: primaryGuestData.name,
          email: primaryGuestData.email,
          phone: primaryGuestData.phone || null,
          gender: primaryGuestData.gender
        },
        rooms,
        beds,
        courses,
        addOns,
        guestCount: formData.guestCount || 1,
        notes: (formData.notes || '') + guestsNotes,
        adminNote: formData.adminNote || '',
        reason: formData.reason,
        paymentStatus: formData.paymentStatus
      };

      console.log('Creating Super Admin Past Date Booking (New Format):', {
        ...reservationData,
        isOMGMode: isOMGExperienceMode
      });

      let response;
      if (isOMGExperienceMode) {
        console.log('Using OMG Experience past-date booking endpoint');
        // TODO: Use dedicated OMG Experience past-date endpoint when available
        // For now, use regular past date booking - backend should detect OMG-eligible rooms
        response = await superAdminService.createPastDateBooking(reservationData);
        toast.success(`Successfully created OMG Experience past date booking with ${rooms.length} room(s) and ${beds.length} bed(s) (20% markup applied)!`, {
          autoClose: 5000
        });
      } else {
        response = await superAdminService.createPastDateBooking(reservationData);
        toast.success(`Successfully created past date booking with ${rooms.length} room(s) and ${beds.length} bed(s)!`);
      }
      
      // Reset form
      setFormData({
        checkInDate: '',
        checkOutDate: '',
        courseId: '',
        courseCount: 1,
        guestCount: 1,
        addons: [],
        notes: '',
        adminNote: '',
        reason: '',
        paymentStatus: 'PENDING'
      });
      
      setSelectedRooms([]);
      setGuestDetails([]);
      setPrimaryGuest({ name: '', email: '', phone: '', gender: '' });
      setSameGuestForAll(true);
      setSelectedCourse(null);
      setIsOMGExperienceMode(false); // Reset OMG mode
      setTotalPricing({ individualBookings: [], totalAmount: 0, courseFee: 0, addonsTotal: 0 });
      setCurrentStep('course-selection');
      
      if (onReservationCreated) {
        onReservationCreated();
      }
    } catch (error) {
      console.error('Error creating past date reservation:', error);
      
      if (error.response?.status === 409) {
        toast.error(
          `Booking Conflict: One or more selected accommodations are already booked for these dates. Please refresh and select different accommodations.`,
          { autoClose: 8000 }
        );
        setCurrentStep('room-selection');
      } else if (error.response?.status === 400) {
        toast.error(`Validation Error: ${error.response?.data?.error || error.message}`);
      } else if (error.response?.status === 403) {
        toast.error(`Access Denied: Super admin privileges required for past date bookings`);
      } else {
        toast.error(`Failed to create past date booking: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load rooms when dates change
  useEffect(() => {
    if (currentStep === 'room-selection' && formData.checkInDate && formData.checkOutDate) {
      loadAvailableRooms();
    }
  }, [currentStep, formData.checkInDate, formData.checkOutDate, loadAvailableRooms]);

  // Get minimum date (allows past dates for super admin)
  const getMinDate = () => {
    // Super admin can select dates up to 2 years in the past
    return format(subDays(new Date(), 730), 'yyyy-MM-dd');
  };

  // Get max date (current date)
  const getMaxDate = () => {
    return format(new Date(), 'yyyy-MM-dd');
  };

  const getMinCheckOutDate = () => {
    if (!formData.checkInDate) return getMinDate();
    return format(addDays(new Date(formData.checkInDate), 1), 'yyyy-MM-dd');
  };

  // Render course selection step
  const renderCourseSelection = () => (
    <div className="course-selection-step">
      <div style={{ marginBottom: '30px' }}>
        <div style={{ backgroundColor: '#F2D9D3', border: '1px solid #D48A7C', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '20px' }}><ShieldIcon size="1em" aria-hidden="true" /></span>
            <h3 style={{ margin: 0, color: '#8E3628' }}>Super Admin Past Date Booking</h3>
          </div>
          <p style={{ margin: 0, color: '#481B14', fontSize: '14px' }}>
            You can create bookings for past dates. This feature is restricted to super administrators only.
          </p>
        </div>
        
        <h2 style={{ color: '#36433B', borderBottom: '3px solid #1A3E2A', paddingBottom: '10px' }}>
          Step 1: Select Course or Staycation
        </h2>
        <p style={{ color: '#5F6B61', marginTop: '10px' }}>
          Choose from available courses or skip for staycation booking
        </p>
      </div>

      <div style={{ display: 'grid', gap: '20px', marginBottom: '30px' }}>
        {/* OMG Experience Option - NEW */}
        <div 
          style={{
            padding: '24px',
            border: '3px solid #A9613C',
            borderRadius: '12px',
            background: '#F8F0EA',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden'
          }}
          onClick={() => handleOMGExperienceSelect()}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = '#975534';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 16px rgba(169, 97, 60, 0.2)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = '#A9613C';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          {/* Badge */}
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            backgroundColor: '#A9613C',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '700',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            +20% Premium
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <span style={{ fontSize: '28px' }}><SparklesIcon size="1em" aria-hidden="true" /></span>
            <h3 style={{ margin: 0, color: '#663923', fontWeight: '700' }}>OMG Experience (Past Date)</h3>
          </div>
          
          <p style={{ color: '#663923', margin: '0 0 12px 0', fontWeight: '500' }}>
            Premium shared room staycation with enhanced pricing for past dates
          </p>
          
          <div style={{ 
            backgroundColor: '#ffffff', 
            padding: '12px', 
            borderRadius: '8px',
            border: '1px solid #E4C2AC'
          }}>
            <div style={{ fontSize: '13px', color: '#975534', marginBottom: '8px' }}>
              <strong>Includes:</strong>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#663923', fontSize: '13px' }}>
              <li>Daya Sharing & Meera Sharing rooms only</li>
              <li>20% premium markup on accommodation</li>
              <li>No course required - flexible past dates</li>
              <li>Enhanced staycation experience</li>
            </ul>
          </div>
        </div>

        {/* Staycation Option */}
        <div 
          style={{
            padding: '24px',
            border: '2px solid #A13F2F',
            borderRadius: '12px',
            backgroundColor: '#F9EEEB',
            cursor: 'pointer',
            transition: 'all 0.3s ease'
          }}
          onClick={() => handleCourseSelect(null)}
          onMouseOver={(e) => e.currentTarget.style.borderColor = '#7E472C'}
          onMouseOut={(e) => e.currentTarget.style.borderColor = '#A13F2F'}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <span style={{ fontSize: '24px' }}><PalmtreeIcon size="1em" aria-hidden="true" /></span>
            <h3 style={{ margin: 0, color: '#36433B' }}>Staycation (No Course)</h3>
          </div>
          <p style={{ color: '#5F6B61', margin: 0 }}>
            Flexible accommodation without course commitment. You'll set custom past dates.
          </p>
        </div>

        {/* Available Courses */}
        {courses.length > 0 && courses.map(course => {
          const courseType = course.startDate && course.endDate ? 'fixed' : 'duration';
          
          return (
            <div 
              key={course.id}
              style={{
                padding: '24px',
                border: `2px solid ${courseType === 'fixed' ? '#1A3E2A' : '#1A3E2A'}`,
                borderRadius: '12px',
                backgroundColor: '#F5F3EB',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onClick={() => handleCourseSelect(course)}
              onMouseOver={(e) => e.currentTarget.style.borderColor = courseType === 'fixed' ? '#163626' : '#163626'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = courseType === 'fixed' ? '#1A3E2A' : '#1A3E2A'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '24px' }}><Flower2Icon size="1em" aria-hidden="true" /></span>
                    <h3 style={{ margin: 0, color: '#36433B' }}>{course.name}</h3>
                    {/* Course Type Badge */}
                    <span style={{
                      fontSize: '12px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      backgroundColor: courseType === 'fixed' ? '#E1E9E2' : '#E1E9E2',
                      color: courseType === 'fixed' ? '#0F2619' : '#163626',
                      fontWeight: '500'
                    }}>
                      {courseType === 'fixed' ? 'Fixed Date' : 'Duration-Based'}
                    </span>
                  </div>
                  <p style={{ color: '#5F6B61', margin: 0, fontSize: '14px' }}>
                    {course.description || 'Meditation and mindfulness retreat'}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#163626' }}>
                    ₹{course.fee || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#5F6B61' }}>Course Fee</div>
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '14px' }}>
                {courseType === 'fixed' ? (
                  <>
                    <div>
                      <strong>Start:</strong> {format(new Date(course.startDate), 'MMM dd, yyyy')}
                    </div>
                    <div>
                      <strong>End:</strong> {format(new Date(course.endDate), 'MMM dd, yyyy')}
                    </div>
                  </>
                ) : (
                  <>
                    {course.duration && (
                      <div>
                        <strong>Duration:</strong> {course.duration} days
                      </div>
                    )}
                    <div>
                      <strong>Type:</strong> Flexible dates
                    </div>
                  </>
                )}
              </div>
              
              <div style={{ 
                marginTop: '12px', 
                fontSize: '12px', 
                fontWeight: 'bold',
                color: '#975534',
                padding: '8px',
                backgroundColor: '#F1E0D4',
                borderRadius: '6px'
              }}>
                Available for past date booking (Super Admin only)
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Render room selection step
  const renderRoomSelection = () => (
    <div className="room-selection-step" style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 20, alignItems: 'start' }}>
      <div>
        <div style={{ marginBottom: '30px' }}>
          <button 
            onClick={() => setCurrentStep('course-selection')}
            style={{
              backgroundColor: '#5F6B61',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              marginBottom: '20px'
            }}
          >
            ← Back to Course Selection
          </button>
          
          <h2 style={{ color: '#36433B', borderBottom: '3px solid #1A3E2A', paddingBottom: '10px' }}>
            Step 2: Select Accommodation & Past Dates
          </h2>
          
          {selectedCourse ? (
            <div style={{ backgroundColor: '#E1E9E2', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#163626' }}>Selected Course: {selectedCourse.name}</h4>
              <p style={{ margin: 0, color: '#5F6B61' }}>Duration-Based Course</p>
            </div>
          ) : null}
          
          {/* OMG Experience Mode Indicator */}
          {isOMGExperienceMode && (
            <div style={{
              backgroundColor: '#F1E0D4',
              border: '2px solid #A9613C',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '24px' }}><SparklesIcon size="1em" aria-hidden="true" /></span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '700', color: '#663923', marginBottom: '4px' }}>
                  OMG Experience Mode Active (Past Date)
                </div>
                <div style={{ fontSize: '13px', color: '#975534' }}>
                  Premium 20% markup • Daya & Meera Sharing rooms only • Past date booking
                </div>
              </div>
              <button
                onClick={() => {
                  setIsOMGExperienceMode(false);
                  setCurrentStep('course-selection');
                }}
                style={{
                  backgroundColor: 'white',
                  border: '1px solid #A9613C',
                  color: '#A9613C',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                Change
              </button>
            </div>
          )}

          {/* Past Date Selection */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ backgroundColor: '#F3E6CB', border: '1px solid #B68235', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '20px' }}><AlarmClockIcon size="1em" aria-hidden="true" /></span>
                <h4 style={{ margin: 0, color: '#684A1F' }}>Past Date Selection</h4>
              </div>
              <p style={{ margin: 0, color: '#4F3818', fontSize: '14px' }}>
                Select any date in the past (up to 2 years ago). This is only available to super administrators.
              </p>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Check-in Date *</label>
                <input
                  type="date"
                  name="checkInDate"
                  value={formData.checkInDate}
                  onChange={handleInputChange}
                  min={getMinDate()}
                  max={getMaxDate()}
                  required
                  style={{ width: '100%', padding: '8px', border: '1px solid #E3DFD3', borderRadius: '4px' }}
                />
                <small style={{ color: '#5F6B61', fontSize: '12px' }}>Can be up to 2 years ago</small>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Check-out Date *</label>
                <input
                  type="date"
                  name="checkOutDate"
                  value={formData.checkOutDate}
                  onChange={handleInputChange}
                  min={getMinCheckOutDate()}
                  required
                  style={{ width: '100%', padding: '8px', border: '1px solid #E3DFD3', borderRadius: '4px' }}
                />
                <small style={{ color: '#5F6B61', fontSize: '12px' }}>Must be after check-in, can be in the future</small>
              </div>
            </div>
          </div>
        </div>

        {/* Available Rooms */}
        {loadingRooms ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '20px', marginBottom: '16px' }}><RefreshCwIcon size="1em" aria-hidden="true" /></div>
            <div>Checking past date availability...</div>
          </div>
        ) : availableRooms.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#F3E6CB', borderRadius: '8px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}><HomeIcon size="1em" aria-hidden="true" /></div>
            <p>No rooms available for the selected past dates. Please try different dates.</p>
          </div>
        ) : isOMGExperienceMode && availableRooms.filter(room => omgExperienceService.isSharedRoomType(room.roomType?.name)).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#F8F0EA', borderRadius: '8px', border: '2px solid #A9613C' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}><SparklesIcon size="1em" aria-hidden="true" /></div>
            <h3 style={{ color: '#663923', marginBottom: '8px' }}>No OMG Experience Rooms Available</h3>
            <p style={{ color: '#975534', marginBottom: '16px' }}>
              Daya Sharing and Meera Sharing rooms are not available for the selected past dates.
            </p>
            <button
              onClick={() => {
                setIsOMGExperienceMode(false);
                setCurrentStep('course-selection');
              }}
              style={{
                backgroundColor: '#A9613C',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              ← Choose Different Option
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '20px' }}>
            {availableRooms
              .filter(room => {
                // In OMG Experience mode, show only Daya Sharing and Meera Sharing
                if (isOMGExperienceMode) {
                  return omgExperienceService.isSharedRoomType(room.roomType?.name);
                }
                // Otherwise show all rooms
                return true;
              })
              .map(room => {
              const isPrivate = !room.roomType?.isShared;
              const isOMGEligible = omgExperienceService.isSharedRoomType(room.roomType?.name);
              
              return (
                <div key={room.id} style={{
                  border: isOMGExperienceMode && isOMGEligible ? '2px solid #A9613C' : '1px solid #E3DFD3',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: isOMGExperienceMode && isOMGEligible ? '#F8F0EA' : 'white'
                }}>
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <h3 style={{ margin: 0, color: '#36433B' }}>
                            Room {room.roomNumber}
                          </h3>
                          {isOMGExperienceMode && isOMGEligible && (
                            <span style={{
                              backgroundColor: '#A9613C',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '700'
                            }}>
                              OMG
                            </span>
                          )}
                        </div>
                        <div style={{ color: '#5F6B61', fontSize: '14px' }}>
                          {room.roomType?.name || 'Standard Room'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {isOMGExperienceMode && isOMGEligible ? (
                          <>
                            <div style={{ fontSize: '14px', color: '#A5A89B', textDecoration: 'line-through', marginBottom: '4px' }}>
                              ₹{room.roomType?.basePrice || room.roomType?.defaultRate || 100}
                            </div>
                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#A9613C' }}>
                              ₹{Math.round((room.roomType?.basePrice || room.roomType?.defaultRate || 100) * 1.20)}/night
                            </div>
                            <div style={{ fontSize: '11px', color: '#A9613C', fontWeight: '600' }}>
                              +20% OMG Markup
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#163626' }}>
                              ₹{room.roomType?.basePrice || room.roomType?.defaultRate || 100}/night
                            </div>
                            <div style={{ fontSize: '12px', color: '#5F6B61' }}>Base Rate</div>
                          </>
                        )}
                      </div>
                    </div>

                    {isPrivate ? (
                      <button
                        onClick={() => handleAccommodationSelect(room)}
                        style={{
                          backgroundColor: '#1A3E2A',
                          color: 'white',
                          border: 'none',
                          padding: '12px 24px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          width: '100%',
                          fontWeight: 'bold'
                        }}
                      >
                        Add Private Room
                      </button>
                    ) : (
                      <div>
                        <h4 style={{ margin: '0 0 12px 0', color: '#36433B' }}>
                          Available Beds ({room.availableBeds?.length || 0})
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '12px' }}>
                          {room.availableBeds?.map(bed => (
                            <button
                              key={bed.id}
                              onClick={() => handleAccommodationSelect(room, bed)}
                              style={{
                                padding: '16px 8px',
                                border: bed.isFemaleOnly ? '2px solid #8E3628' : '1px solid #E3DFD3',
                                borderRadius: '8px',
                                backgroundColor: bed.isFemaleOnly ? '#F2D9D3' : '#F5F3EB',
                                cursor: 'pointer',
                                textAlign: 'center',
                                fontSize: '12px'
                              }}
                            >
                              <div style={{ fontSize: '20px', marginBottom: '4px' }}><BedDoubleIcon size="1em" aria-hidden="true" /></div>
                              <div style={{ fontWeight: 'bold' }}>Bed {bed.bedNumber}</div>
                              {bed.isFemaleOnly && (
                                <div style={{ color: '#8E3628', fontSize: '10px', marginTop: '4px' }}>
                                  Female Only
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Sticky Selection Sidebar */}
      <aside style={{ position: 'sticky', top: 16, alignSelf: 'start' }}>
        <div style={{ border: '1px solid #E3DFD3', borderRadius: 12, background: 'white', padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, color: '#1A3E2A' }}>Selected ({selectedRooms.length})</h3>
            {selectedRooms.length > 0 && (
              <button
                onClick={() => { setSelectedRooms([]); setGuestDetails([]); }}
                style={{ background: 'transparent', border: 'none', color: '#A13F2F', cursor: 'pointer', fontSize: 12 }}
                title="Clear all"
              >
                Clear
              </button>
            )}
          </div>

          {selectedRooms.length === 0 ? (
            <div style={{ color: '#5F6B61', fontSize: 13 }}>No selections yet</div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {selectedRooms.map((sel, idx) => (
                <div key={sel.id} style={{ border: '1px solid #E3DFD3', borderRadius: 8, padding: 10, position: 'relative' }}>
                  <button
                    onClick={() => removeRoomSelection(idx)}
                    style={{ position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none', cursor: 'pointer', color: '#A5A89B' }}
                    title="Remove"
                  >
                    ✕
                  </button>
                  <div style={{ fontSize: 13, color: '#1A3E2A', fontWeight: 600 }}>
                    {sel.bed ? `Room ${sel.room.roomNumber} • Bed ${sel.bed.bedNumber}` : `Room ${sel.room.roomNumber}`}
                  </div>
                  <div style={{ fontSize: 12, color: '#5F6B61' }}>{sel.room.roomType?.name}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: 12, borderTop: '1px solid #E3DFD3', paddingTop: 12 }}>
            <button
              onClick={proceedToGuestDetails}
              disabled={selectedRooms.length === 0}
              style={{
                width: '100%',
                backgroundColor: selectedRooms.length === 0 ? '#A5A89B' : '#1A3E2A',
                color: 'white',
                border: 'none',
                padding: '10px 14px',
                borderRadius: 8,
                cursor: selectedRooms.length === 0 ? 'not-allowed' : 'pointer',
                fontWeight: 700
              }}
            >
              Continue →
            </button>
          </div>
        </div>
      </aside>
    </div>
  );

  // Render guest details step
  const renderGuestDetails = () => (
    <div className="guest-details-step" style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 20, alignItems: 'start' }}>
      <div>
        <div style={{ marginBottom: 20 }}>
          <button 
            onClick={() => setCurrentStep('room-selection')}
            style={{ backgroundColor: '#5F6B61', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}
          >
            ← Back to Rooms
          </button>
        </div>

        {/* Past Date Booking Notice */}
        <div style={{ backgroundColor: '#F2D9D3', border: '1px solid #D48A7C', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '20px' }}><CalendarIcon size="1em" aria-hidden="true" /></span>
            <h4 style={{ margin: 0, color: '#8E3628' }}>Past Date Booking Details</h4>
          </div>
          <div style={{ color: '#481B14', fontSize: '14px' }}>
            <strong>Dates:</strong> {format(new Date(formData.checkInDate), 'MMM dd, yyyy')} - {format(new Date(formData.checkOutDate), 'MMM dd, yyyy')}
            {selectedCourse && (
              <div style={{ marginTop: '4px' }}>
                <strong>Course:</strong> {selectedCourse.name}
              </div>
            )}
          </div>
        </div>

        {/* Guest Count and Course Count */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: 16, 
          marginBottom: 20,
          padding: 16,
          backgroundColor: '#F5F3EB',
          borderRadius: 8,
          border: '1px solid #E3DFD3'
        }}>
          <CounterInput
            label="Guest Count"
            value={formData.guestCount || 1}
            onChange={(value) => setFormData(prev => ({ ...prev, guestCount: value }))}
            min={1}
            max={10}
            helperText="Number of guests in the booking"
            style={{ width: '100%' }}
          />
          
          <CounterInput
            label={!selectedCourse ? "Course Count" : "Course Count"}
            value={formData.courseCount || 0}
            onChange={(value) => setFormData(prev => ({ ...prev, courseCount: value }))}
            min={selectedCourse ? 1 : 0}
            max={5}
            disabled={!selectedCourse}
            helperText={selectedCourse ? 'Number of course enrollments' : 'Locked at 0 for staycation'}
            style={{ width: '100%' }}
          />
        </div>

        <div style={{ border: '1px solid #E3DFD3', borderRadius: 8, padding: 16, marginBottom: 20 }}>
          <h4 style={{ marginTop: 0 }}>Guest Details</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <input placeholder="Full Name *" value={primaryGuest.name} onChange={(e) => handlePrimaryGuestChange('name', e.target.value)} style={{ padding: 10, border: '1px solid #D2CDBF', borderRadius: 6 }} />
            <input placeholder="Email *" type="email" value={primaryGuest.email} onChange={(e) => handlePrimaryGuestChange('email', e.target.value)} style={{ padding: 10, border: '1px solid #D2CDBF', borderRadius: 6 }} />
            <input placeholder="Phone" value={primaryGuest.phone} onChange={(e) => handlePrimaryGuestChange('phone', e.target.value)} style={{ padding: 10, border: '1px solid #D2CDBF', borderRadius: 6 }} />
            <select value={primaryGuest.gender} onChange={(e) => handlePrimaryGuestChange('gender', e.target.value)} style={{ padding: 10, border: '1px solid #D2CDBF', borderRadius: 6 }}>
              <option value="">Select Gender *</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div style={{ color: '#5F6B61', fontSize: 12, marginTop: 6 }}>These details will apply to all selected rooms.</div>
        </div>

        {/* Past Date Booking Specific Fields */}
        <div style={{ marginTop: 20, border: '2px solid #D48A7C', borderRadius: 8, padding: 16, backgroundColor: '#F9EEEB' }}>
          <h4 style={{ marginTop: 0, color: '#8E3628' }}>Past Date Booking Information</h4>
          
          <div style={{ display: 'grid', gap: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Reason for Past Date Booking *</label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                rows={2}
                placeholder="Explain why this past date booking is being created (minimum 3 characters)"
                required
                style={{ width: '100%', padding: 10, border: '1px solid #D2CDBF', borderRadius: 6, resize: 'vertical' }}
              />
              <small style={{ color: '#481B14', fontSize: 12 }}>Required: Provide a clear reason for creating this past date booking</small>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Admin Note</label>
              <textarea
                name="adminNote"
                value={formData.adminNote}
                onChange={handleInputChange}
                rows={2}
                placeholder="Internal admin notes (optional)"
                style={{ width: '100%', padding: 10, border: '1px solid #D2CDBF', borderRadius: 6, resize: 'vertical' }}
              />
            </div>
          </div>
        </div>

        {/* Payment Status & Notes */}
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Payment Status</label>
            <select
              name="paymentStatus"
              value={formData.paymentStatus}
              onChange={handleInputChange}
              style={{ width: '100%', padding: 10, border: '1px solid #D2CDBF', borderRadius: 6 }}
            >
              <option value="PENDING">Pending</option>
              <option value="SUCCESS">Paid</option>
              <option value="FAILED">Failed</option>
              <option value="REFUNDED">Refunded</option>
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Guest Notes</label>
            <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={3} placeholder="Any special notes or requirements" style={{ width: '100%', padding: 10, border: '1px solid #D2CDBF', borderRadius: 6, resize: 'vertical' }} />
          </div>
        </div>
      </div>

      {/* Summary Sidebar */}
      <aside style={{ position: 'sticky', top: 16, alignSelf: 'start' }}>
        <div style={{ 
          border: isOMGExperienceMode ? '2px solid #A9613C' : '1px solid #E3DFD3', 
          borderRadius: 12, 
          background: isOMGExperienceMode ? '#F8F0EA' : 'white', 
          padding: 16 
        }}>
          {/* OMG Experience Badge */}
          {isOMGExperienceMode && (
            <div style={{
              backgroundColor: '#A9613C',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '8px',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              fontWeight: '700'
            }}>
              <span style={{ fontSize: '16px' }}><SparklesIcon size="1em" aria-hidden="true" /></span>
              OMG Experience Active (Past Date)
              <span style={{ marginLeft: 'auto', fontSize: '11px', opacity: 0.9 }}>+20%</span>
            </div>
          )}

          <h3 style={{ 
            marginTop: 0, 
            marginBottom: 8,
            color: isOMGExperienceMode ? '#663923' : 'inherit'
          }}>Booking Summary</h3>
          
          <div style={{ fontSize: 14, color: '#5F6B61', marginBottom: 16 }}>
            <div><strong>Check-in:</strong> {formData.checkInDate ? format(new Date(formData.checkInDate), 'MMM dd, yyyy') : 'Not set'}</div>
            <div><strong>Check-out:</strong> {formData.checkOutDate ? format(new Date(formData.checkOutDate), 'MMM dd, yyyy') : 'Not set'}</div>
            {formData.checkInDate && formData.checkOutDate && (
              <div><strong>Nights:</strong> {differenceInDays(new Date(formData.checkOutDate), new Date(formData.checkInDate))}</div>
            )}
            <div><strong>Rooms:</strong> {selectedRooms.length}</div>
            <div><strong>Guests:</strong> {formData.guestCount || 1}</div>
            {selectedCourse && <div><strong>Course:</strong> {selectedCourse.name}</div>}
          </div>

          {/* Pricing Breakdown */}
          {(totalPricing.totalAmount > 0 || calculatingPrice) && (
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#F5F3EB', borderRadius: 8, border: '1px solid #E3DFD3' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 16, fontWeight: 600, color: '#1A3E2A' }}>Pricing Breakdown</h4>
              
              {calculatingPrice ? (
                <div style={{ textAlign: 'center', color: '#5F6B61', padding: '8px 0' }}>
                  <div style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #E3DFD3', borderTop: '2px solid #1A3E2A', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                  <span style={{ marginLeft: 8 }}>Calculating...</span>
                </div>
              ) : (
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                  {/* Room costs */}
                  {totalPricing.individualBookings && totalPricing.individualBookings.length > 0 && (
                    <>
                      <div style={{ fontWeight: 600, marginBottom: 8, color: '#36433B' }}>Accommodation Details:</div>
                      {totalPricing.individualBookings.map((booking, index) => (
                        <div key={index} style={{ 
                          marginBottom: 12, 
                          paddingLeft: 12, 
                          paddingBottom: 8,
                          borderBottom: index < totalPricing.individualBookings.length - 1 ? '1px solid #EEEBE1' : 'none',
                          backgroundColor: booking.isOMGPricing ? '#F8F0EA' : 'transparent',
                          paddingRight: 8,
                          borderRadius: 6
                        }}>
                          <div style={{ fontWeight: 600, color: '#24332A', marginBottom: 6, fontSize: 13 }}>
                            {booking.name}
                            {booking.isOMGPricing && (
                              <span style={{ 
                                marginLeft: 6, 
                                fontSize: 10, 
                                backgroundColor: '#A9613C', 
                                color: 'white', 
                                padding: '2px 6px', 
                                borderRadius: 8,
                                fontWeight: 700
                              }}>OMG</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2, color: '#5F6B61', fontSize: 12 }}>
                            <span>Base Rate:</span>
                            <span>₹{booking.rate}/night</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2, color: '#5F6B61', fontSize: 12 }}>
                            <span>Nights:</span>
                            <span>× {booking.nights}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, color: '#36433B', fontWeight: 600, paddingTop: 4, borderTop: '1px dashed #E3DFD3' }}>
                            <span>Base Total:</span>
                            <span>₹{Math.round(booking.basePrice)}</span>
                          </div>
                          {booking.omgMarkup > 0 && (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2, color: '#A9613C', fontWeight: 700 }}>
                                <span>OMG Markup:</span>
                                <span>+₹{Math.round(booking.omgMarkup)}</span>
                              </div>
                            </>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 14, color: booking.isOMGPricing ? '#A9613C' : '#163626', marginTop: 6, paddingTop: 6, borderTop: '2px solid ' + (booking.isOMGPricing ? '#A9613C' : '#163626') }}>
                            <span>Subtotal:</span>
                            <span>₹{Math.round(booking.total)}</span>
                          </div>
                        </div>
                      ))}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        fontWeight: 700, 
                        marginTop: 12,
                        paddingTop: 12,
                        borderTop: '2px solid #E3DFD3',
                        color: '#1A3E2A',
                        fontSize: 15
                      }}>
                        <span>Total Accommodation:</span>
                        <span>₹{Math.round(totalPricing.roomsTotal || 0)}</span>
                      </div>
                      
                      {/* Show total OMG markup summary if applicable */}
                      {totalPricing.hasOMGMarkup && totalPricing.totalOMGMarkup > 0 && (
                        <div style={{
                          backgroundColor: '#F1E0D4',
                          border: '1px solid #A9613C',
                          borderRadius: 6,
                          padding: 10,
                          marginTop: 12,
                          marginBottom: 8
                        }}>
                          <div style={{ fontSize: 13, color: '#663923', fontWeight: 700, marginBottom: 6 }}>
                            OMG Experience Summary
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#975534', marginBottom: 2 }}>
                            <span>Total Base Price:</span>
                            <span>₹{Math.round(totalPricing.totalBasePrice || 0)}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#A9613C', fontWeight: 700, marginBottom: 4 }}>
                            <span>Total OMG Markup:</span>
                            <span>+₹{Math.round(totalPricing.totalOMGMarkup || 0)}</span>
                          </div>
                          {totalPricing.markupInfo && (
                            <div style={{ fontSize: 11, color: '#975534', fontStyle: 'italic', lineHeight: 1.4 }}>
                              {totalPricing.markupInfo}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Course fee */}
                  {totalPricing.courseFee > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span>Course Fee ({formData.guestCount} guest{formData.guestCount > 1 ? 's' : ''})</span>
                      <span>₹{totalPricing.courseFee}</span>
                    </div>
                  )}

                  {/* Addons */}
                  {totalPricing.addonsTotal > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span>Add-ons</span>
                      <span>₹{totalPricing.addonsTotal}</span>
                    </div>
                  )}

                  {/* Total */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    fontWeight: 700, 
                    fontSize: 16, 
                    marginTop: 8, 
                    paddingTop: 8, 
                    borderTop: '2px solid #1A3E2A',
                    color: '#24332A',
                    backgroundColor: totalAmountChanged ? '#E1E9E2' : 'transparent',
                    transition: 'background-color 0.5s ease',
                    padding: '8px 4px'
                  }}>
                    <span>Total Amount:</span>
                    <span>₹{totalPricing.totalAmount}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ borderTop: '1px solid #E3DFD3', paddingTop: 12 }}>
            <button
              onClick={handleSubmit}
              disabled={loading || selectedRooms.length === 0 || !formData.reason?.trim()}
              style={{
                width: '100%',
                backgroundColor: loading || selectedRooms.length === 0 || !formData.reason?.trim() ? '#A5A89B' : '#8E3628',
                color: 'white',
                border: 'none',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: loading || selectedRooms.length === 0 || !formData.reason?.trim() ? 'not-allowed' : 'pointer',
                fontWeight: 800,
                marginTop: 12
              }}
            >
              {loading ? 'Creating…' : `Create Past Date Booking${selectedRooms.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );

  return (
    <div className="super-admin-past-date-reservation">
      {currentStep === 'course-selection' && renderCourseSelection()}
      {currentStep === 'room-selection' && renderRoomSelection()}
      {currentStep === 'guest-details' && renderGuestDetails()}
    </div>
  );
}

export default SuperAdminPastDateReservation;
